import type { ReactElement, ReactNode } from 'react'

import { BrandLogo } from '@/components/layout/brand-logo'
import { ThemeToggle } from '@/components/theme/theme-toggle'
import { cn } from '@/lib/utils'

export interface AuthLayoutProps {
  readonly titulo: string
  readonly descricao?: string | undefined
  readonly children: ReactNode
  /** Links auxiliares abaixo do formulário (voltar, ajuda). */
  readonly rodape?: ReactNode | undefined
  /** Telas com mais conteúdo (setup de 2FA) pedem uma coluna mais larga. */
  readonly largura?: 'estreita' | 'larga' | undefined
}

/**
 * Casca das telas de identidade (login, 2FA, senha).
 *
 * Não usa o `AppShell`: quem está aqui não tem sessão — ou tem uma sessão que
 * ainda não pode navegar — e uma sidebar cheia de módulos inacessíveis só
 * ofereceria caminhos que terminam em 403.
 *
 * A partir de `lg` a tela vira um split: painel de marca texturizado à
 * esquerda (o único amarelo é o traço) e o formulário respirando sobre a
 * superfície à direita, sem cartão. Abaixo de `lg` o formulário é um painel
 * de superfície sobre a textura fraca.
 */
export function AuthLayout({
  titulo,
  descricao,
  children,
  rodape,
  largura = 'estreita',
}: AuthLayoutProps): ReactElement {
  const larga = largura === 'larga'

  return (
    <div className="flex min-h-full flex-col bg-blindagem lg:flex-row">
      {/* Topo mobile/tablet — no desktop cada lado tem o seu canto. */}
      <div className="flex items-center justify-between p-4 print:hidden lg:hidden">
        <BrandLogo />
        <ThemeToggle />
      </div>

      {/* Painel de marca: textura protagonista, monocromático fora o traço.
          Sticky + h-dvh: se a coluna do formulário rolar (2FA), ele fica. */}
      <aside
        className={cn(
          'relative hidden shrink-0 flex-col justify-between overflow-hidden',
          'border-r border-border bg-blindagem-forte p-10',
          'lg:sticky lg:top-0 lg:flex lg:h-dvh',
          larga ? 'lg:w-1/3 lg:max-w-xl' : 'lg:w-[44%] lg:max-w-2xl',
        )}
      >
        {/* A hairline nasce no logo e corre até a borda — detalhe de planta. */}
        <div className="flex items-center gap-4">
          <BrandLogo className="h-12 shrink-0" />
          <div aria-hidden="true" className="h-px flex-1 bg-border-strong" />
        </div>

        <div className="space-y-4">
          <div aria-hidden="true" className="h-0.5 w-10 rounded-full bg-primary" />
          <p className="text-xs font-medium uppercase tracking-[0.2em] text-fg-muted">
            Painel da operação
          </p>
          <p className="max-w-sm text-2xl font-semibold leading-snug text-foreground">
            Frota blindada sob controle total.
          </p>
          <p className="max-w-sm text-sm text-fg-secondary">
            Reservas, vistorias e faturamento da Locaveri em um único painel.
          </p>
        </div>

        <p className="text-xs text-fg-muted">Acesso restrito à equipe da operação.</p>
      </aside>

      {/* Lado do formulário: um degrau de luminosidade acima do painel preto —
          a hierarquia vem da superfície, não de sombra. */}
      <main className="relative flex min-w-0 flex-1 flex-col lg:bg-surface">
        <div className="hidden justify-end p-4 print:hidden lg:flex">
          <ThemeToggle />
        </div>

        <div
          className={cn(
            'flex flex-1 items-start justify-center px-4 pb-16 pt-4',
            'sm:items-center sm:pt-0 lg:px-10',
          )}
        >
          <div className={cn('w-full', larga ? 'max-w-xl' : 'max-w-sm')}>
            {/* Abaixo de lg o formulário é um painel de superfície sobre a
                textura; no desktop ele larga a moldura e senta na superfície. */}
            <div
              className={cn(
                'rounded-xl border border-border bg-surface p-5 sm:p-6',
                'lg:rounded-none lg:border-0 lg:bg-transparent lg:p-0',
                'print:rounded-none print:border-0 print:p-0',
              )}
            >
              <header className="mb-6 space-y-1.5">
                <h1 className="text-xl font-semibold text-foreground">{titulo}</h1>
                {descricao === undefined ? null : (
                  <p className="text-sm text-fg-secondary">{descricao}</p>
                )}
              </header>
              <div className="space-y-4">{children}</div>
            </div>

            {rodape === undefined ? null : (
              <div className="mt-4 text-center text-sm text-fg-secondary print:hidden">
                {rodape}
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  )
}
